<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-overlay :value="isLoading" z-index="999" :absolute="embedded">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-overlay>

      <component :is="embedded ? 'div' : 'v-card'" v-bind="embedded ? {} : { elevation: 6, rounded: 'xxl' }" :class="embedded ? '' : 'app-card'" v-if="order">
        <v-card-title v-if="!embedded">
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon> </v-btn
          >Purchase Order {{ order.po_number }}
          <v-chip class="ml-3" small :color="billingStatusColor(order.billing_status)" text-color="white">
            {{ billingStatusLabels[order.billing_status] }}
          </v-chip>
          <v-chip class="ml-2" small :color="paymentStatusColor(order.payment_status)" text-color="white">
            {{ paymentStatusLabels[order.payment_status] }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn v-if="bill" outlined color="primary" :loading="isDownloading" @click="downloadInvoice">
            <v-icon left small>mdi-file-download-outline</v-icon> Download Invoice
          </v-btn>
        </v-card-title>
        <component :is="embedded ? 'div' : 'v-card-text'">
          <div class="d-flex justify-end mb-4" v-if="embedded && bill">
            <v-btn outlined color="primary" small :loading="isDownloading" @click="downloadInvoice">
              <v-icon left small>mdi-file-download-outline</v-icon> Download Invoice
            </v-btn>
          </div>

          <v-row dense>
            <v-col cols="6" sm="4">
              <AppInfoCard icon="mdi-domain" label="Vendor" :value="order.vendor ? order.vendor.name : '-'" />
            </v-col>
            <v-col cols="6" sm="4">
              <AppInfoCard icon="mdi-calendar" label="PO Date" :value="$options.filters.niceDate(order.po_date) || '-'" />
            </v-col>
            <v-col cols="6" sm="4">
              <AppInfoCard icon="mdi-calendar-clock" label="Expected Delivery" :value="$options.filters.niceDate(order.expected_delivery_date) || '-'" />
            </v-col>
            <v-col cols="12" v-if="order.remarks">
              <AppInfoCard icon="mdi-note-text" label="Remarks" :value="order.remarks" />
            </v-col>
          </v-row>

          <!-- Order Items -->
          <AppSectionHeader label="Order Items" :count="items.length" />
          <v-card outlined v-if="items.length">
            <v-divider></v-divider>
            <div v-for="(item, index) in items" :key="item.id">
              <v-row align="center" class="px-4 py-2" no-gutters>
                <v-col cols="auto">
                  <v-avatar color="deep-purple lighten-4" size="32">
                    <span class="deep-purple--text font-weight-bold">{{ index + 1 }}</span>
                  </v-avatar>
                </v-col>
                <v-col class="ml-4">
                  <div class="font-weight-medium d-flex align-center flex-wrap">
                    <span>
                      {{ item.product_name }}
                      <span v-if="item.variant_name" class="font-weight-regular grey--text"> - {{ item.variant_name }}</span>
                    </span>
                    <v-btn
                      v-if="item.product_id && can('PRODUCTS.SINGLE_VIEW')"
                      text
                      x-small
                      color="success"
                      class="ml-2"
                      :to="`/products/${encryptIt(item.product_id)}/qr-codes`"
                    >
                      <v-icon x-small left>mdi-qrcode</v-icon>View QR
                    </v-btn>
                  </div>
                  <div class="text-caption grey--text">
                    <span v-if="item.sku">{{ item.sku }}</span>
                    <span v-if="Number(item.gst_percentage) > 0"> (+{{ Number(item.gst_percentage).toFixed(2) }}% GST)</span>
                  </div>
                </v-col>
                <v-col cols="auto" class="mx-4">
                  Qty: <span class="font-weight-medium">{{ item.ordered_quantity }}</span>
                </v-col>
                <v-col cols="auto" class="mx-4 grey--text mono-figure">
                  @ ₹{{ Number(item.purchase_price).toFixed(2) }}
                </v-col>
                <v-col cols="auto" class="mx-4 font-weight-bold primary--text mono-figure">
                  ₹{{ lineTotal(item) }}
                </v-col>
              </v-row>
              <v-divider v-if="index < items.length - 1"></v-divider>
            </div>
            <v-divider></v-divider>
            <div class="px-4 py-3">
              <div class="d-flex justify-space-between">
                <span class="grey--text">Subtotal</span>
                <span class="mono-figure">₹{{ totals.subtotal_amount }}</span>
              </div>
              <div class="d-flex justify-space-between">
                <span class="grey--text">GST<span v-if="totals.gst_label"> ({{ totals.gst_label }})</span></span>
                <span class="mono-figure">₹{{ totals.gst_amount }}</span>
              </div>
              <v-divider class="my-1"></v-divider>
              <div class="d-flex justify-space-between font-weight-bold">
                <span>Total</span>
                <span class="primary--text mono-figure">₹{{ totals.total_amount }}</span>
              </div>
            </div>
          </v-card>

          <template v-if="bill">
            <v-row class="mt-6">
              <v-col sm="3">
                <div class="text-caption grey--text">Total</div>
                <div class="text-h6 mono-figure">₹{{ bill.total_amount }}</div>
              </v-col>
              <v-col sm="3">
                <div class="text-caption grey--text">Paid</div>
                <div class="text-h6 mono-figure" style="color: var(--color-success)">₹{{ bill.paid_amount }}</div>
              </v-col>
              <v-col sm="3">
                <div class="text-caption grey--text">Pending</div>
                <div class="text-h6 mono-figure" style="color: var(--color-error)">₹{{ bill.pending_amount }}</div>
              </v-col>
            </v-row>

            <AppSectionHeader label="Record Payment" />
            <v-row align="center" v-if="Number(bill.pending_amount) > 0">
              <v-col sm="2">
                <v-text-field
                  v-model.number="payment.amount"
                  type="number"
                  min="0.01"
                  label="Amount"
                  outlined
                  dense
                  hide-details
                  :error-messages="errors.amount"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-menu
                  v-model="paymentDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="payment.payment_date"
                      outlined
                      readonly
                      dense
                      hide-details
                      label="Payment Date"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="payment.payment_date"
                    @input="paymentDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col sm="2">
                <v-select
                  v-model="payment.payment_mode"
                  :items="paymentModeOptions"
                  label="Mode"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="payment.reference_number"
                  label="Reference No."
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col sm="2">
                <v-btn block color="primary" :loading="isRecording" @click="recordPayment">
                  <v-icon left small>mdi-plus</v-icon> Add
                </v-btn>
              </v-col>
            </v-row>
            <div class="text-caption grey--text mt-2" v-else>
              This purchase order is fully paid.
            </div>
            <div class="text-caption error--text mt-1" v-if="paymentError">
              {{ paymentError }}
            </div>

            <AppSectionHeader label="Payment History" />
            <v-simple-table v-if="bill.payments && bill.payments.length">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Mode</th>
                    <th>Reference</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in bill.payments" :key="`payment_${p.id}`">
                    <td>{{ p.payment_date | niceDate }}</td>
                    <td>{{ paymentModeLabels[p.payment_mode] }}</td>
                    <td>{{ p.reference_number || "-" }}</td>
                    <td class="text-right mono-figure">₹{{ p.amount }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="text-caption grey--text" v-else>No payments recorded yet.</div>
          </template>

          <template v-if="!embedded">
            <v-divider class="mt-5 mb-5"> </v-divider>
            <div class="text-right">
              <v-btn text @click="$router.back()">Back</v-btn>
            </div>
          </template>
        </component>
      </component>
    </component>
  </component>
</template>
<script>
import axios from "axios";
import AppInfoCard from "@/components/AppInfoCard.vue";
import AppSectionHeader from "@/components/AppSectionHeader.vue";
export default {
  name: "PurchaseOrderViewPage",
  components: { AppInfoCard, AppSectionHeader },
  props: {
    // When true, this is rendered inside a dialog (the Purchase Orders list
    // opens it that way on row click) instead of its own routed page - the
    // order id comes from the `orderId` prop rather than the route, and
    // "back" closes the dialog instead of navigating.
    embedded: { type: Boolean, default: false },
    orderId: { type: String, default: null },
  },
  data() {
    return {
      order: null,
      items: [],
      isLoading: false,
      isDownloading: false,
      isRecording: false,
      paymentError: "",
      payment: {
        amount: null,
        payment_date: new Date().toISOString().substr(0, 10),
        payment_mode: 0,
        reference_number: "",
      },
      paymentDateMenu: false,
      paymentModeLabels: {
        0: "Cash",
        1: "Bank Transfer",
        2: "Cheque",
        3: "UPI",
        4: "Other",
      },
      paymentModeOptions: [
        { text: "Cash", value: 0 },
        { text: "Bank Transfer", value: 1 },
        { text: "Cheque", value: 2 },
        { text: "UPI", value: 3 },
        { text: "Other", value: 4 },
      ],
      billingStatusLabels: {
        unbilled: "Unbilled",
        partially_billed: "Partially Billed",
        fully_billed: "Fully Billed",
      },
      paymentStatusLabels: {
        unpaid: "Unpaid",
        partial: "Partial",
        paid: "Paid",
      },
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    // A PO always gets its bill raised at creation time (or via the
    // separate "Record Balance Payment" flow) - this is that bill, the one
    // thing actual payments get recorded against.
    bill() {
      return this.order && this.order.bills && this.order.bills.length
        ? this.order.bills[0]
        : null;
    },
    totals() {
      let subtotal = 0;
      let gst = 0;
      const rates = new Set();
      this.items.forEach((item) => {
        const lineSubtotal =
          (Number(item.ordered_quantity) || 0) *
          (Number(item.purchase_price) || 0);
        const lineGst = this.roundGst(lineSubtotal * ((Number(item.gst_percentage) || 0) / 100));
        subtotal += lineSubtotal;
        gst += lineGst;
        rates.add(Number(item.gst_percentage) || 0);
      });
      let gstLabel = "";
      if (rates.size === 1) {
        gstLabel = `${[...rates][0].toFixed(2)}%`;
      } else if (rates.size > 1) {
        gstLabel = "Multiple Rates";
      }
      return {
        subtotal_amount: subtotal.toFixed(2),
        gst_amount: gst.toFixed(2),
        total_amount: (subtotal + gst).toFixed(2),
        gst_label: gstLabel,
      };
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    billingStatusColor(status) {
      const colors = {
        unbilled: "grey",
        partially_billed: "warning",
        fully_billed: "info",
      };
      return colors[status] || "grey";
    },
    paymentStatusColor(status) {
      const colors = {
        unpaid: "error",
        partial: "warning",
        paid: "success",
      };
      return colors[status] || "grey";
    },
    lineTotal(item) {
      const subtotal =
        (Number(item.ordered_quantity) || 0) * (Number(item.purchase_price) || 0);
      const gst = this.roundGst(subtotal * ((Number(item.gst_percentage) || 0) / 100));
      return (subtotal + gst).toFixed(2);
    },
    // GST rounds to the nearest rupee, tying DOWN at exactly X.50 (unlike
    // standard rounding) - e.g. 99.50 -> 99, 99.51 -> 100, 99.99 -> 100.
    roundGst(value) {
      return Math.ceil(value - 0.5);
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.orderId || this.$route.params.id;
      await axios
        .get(`/purchase_orders/${encryptedId}`)
        .then((response) => {
          this.order = response.data.data;
          this.items = (this.order.items || []).map((item) => ({
            id: item.id,
            product_id: item.product ? item.product.id : null,
            product_name: item.product ? item.product.name : "",
            variant_name: item.productVariant
              ? ((item.productVariant.color || "") + (item.productVariant.size ? " - " + item.productVariant.size : "")).trim()
              : "",
            sku: item.product ? item.product.hsn_code : "",
            ordered_quantity: item.ordered_quantity,
            purchase_price: item.purchase_price,
            gst_percentage: item.gst_percentage,
          }));
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
      this.isLoading = false;
    },
    async recordPayment() {
      if (!this.bill) return;
      try {
        this.isRecording = true;
        this.paymentError = "";
        this.$store.dispatch("errors/setErrors", {});
        await axios.post("/vendor_payments", {
          purchase_bill_id: this.bill.id,
          amount: this.payment.amount,
          payment_date: this.payment.payment_date,
          payment_mode: this.payment.payment_mode,
          reference_number: this.payment.reference_number,
        });
        this.payment = {
          amount: null,
          payment_date: new Date().toISOString().substr(0, 10),
          payment_mode: 0,
          reference_number: "",
        };
        this.isRecording = false;
        await this.getData();
        this.$emit("saved");
      } catch (e) {
        console.log(e);
        this.isRecording = false;
        if (e.response && e.response.data && e.response.data.message) {
          this.paymentError = e.response.data.message;
        }
        if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        }
      }
    },
    async downloadInvoice() {
      if (!this.bill) return;
      this.isDownloading = true;
      try {
        const response = await axios.get(
          `/purchase_bills/${this.encryptIt(this.bill.id)}/invoice/download`,
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `purchase_bill_${this.bill.bill_number}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
      }
      this.isDownloading = false;
    },
  },
};
</script>
